<template>
  <v-container>
    <v-layout>
      <v-flex>
        <v-card class="sms-signin mb-8" outlined>
          <v-app-bar dark color="primary">
            <v-btn
              v-if="smsConfirmationSent && !otpFlow"
              icon
              @click="goToFirstStep"
            >
              <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
            </v-btn>
            <h3>{{ formTitle }}</h3>
            <v-spacer></v-spacer>
          </v-app-bar>
          <v-card-text class="pa-6">
            <div v-show="!smsConfirmationSent">
              <ValidationObserver
                ref="smsStepOneForm"
                v-slot="{ invalid }"
              >
                <v-form @submit.prevent="submitPhoneNumber">
                  <v-container>
                    <v-row class="ma-0">
                      <v-col cols="3" :md="2" class="pl-0">
                        <vue-country-code
                          :disabled-fetching-country="true"
                          :enabled-country-code="true"
                          :preferred-countries="['at', 'de']"
                          default-country="at"
                          @onSelect="onSelectCountryCode"
                        />
                      </v-col>
                      <v-col class="pr-0">
                        <ValidationProvider
                          ref="phoneNumber"
                          name="phoneNumber"
                          vid="phoneNumber"
                          rules="required|phoneNumber"
                          slim
                        >
                          <v-text-field
                            name="phoneNumber"
                            v-model="phoneNumber"
                            type="email"
                            :label="$t('smsSignIn.phoneLabel')"
                            dense
                          />
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </v-container>
                  <p>{{ $t('smsSignIn.stepOneDescription') }}</p>
                  <v-btn
                    type="submit"
                    color="primary"
                    block
                    :disabled="invalid || loading"
                    :loading="loading"
                  >
                    {{$t('auth.nextButton')}}
                  </v-btn>
                </v-form>
              </ValidationObserver>
            </div>
            <div v-show="smsConfirmationSent">
              <ValidationObserver
                ref="smsStepTwoForm"
                v-slot="{ invalid }"
              >
                <v-form @submit.prevent="submitVerificationCode">
                  <div
                    v-if="parsedPhoneNumber || userIdentifierForVerification"
                    class="phone-number-button"
                  >
                    <button @click="goToFirstStep">
                      <v-icon size="20" color="primary">
                        {{ icons.mdiPencil }}
                      </v-icon>
                      {{ parsedPhoneNumber.number || userIdentifierForVerification }}
                    </button>
                  </div>
                  <v-container>
                    <v-row class="ma-0">
                      <v-col cols="7" :md="4" class="px-0">
                        <ValidationProvider
                          ref="verificationCode"
                          name="verificationCode"
                          vid="verificationCode"
                          rules="required|length:6|isNumber"
                          slim
                        >
                          <v-text-field
                            name="verificationCode"
                            v-model="verificationCode"
                            type="email"
                            :label="$t('smsSignIn.codeLabel')"
                            dense
                            @keyup="submitVerificationCode"
                          />
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </v-container>
                  <p>{{ stepTwoDescription }}</p>
                  <v-btn
                    type="submit"
                    color="primary"
                    block
                    :disabled="invalid || loading"
                    :loading="loading"
                  >
                    {{$t('auth.nextButton')}}
                  </v-btn>
                </v-form>
              </ValidationObserver>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog v-model="showDialog" max-width="340px">
      <v-card>
        <v-card-title>
          <h3>{{ dialogTitle }}</h3>
        </v-card-title>
        <v-card-text>
          <p>{{ dialogMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="closeDialog"
          >
            {{ $t('smsSignIn.errorDialogButton') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div id="recaptcha"></div>
  </v-container>
</template>

<script>
import _ from 'lodash'
import {mapGetters, mapActions, mapState} from 'vuex'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import {mdiPencil, mdiArrowLeft} from '@mdi/js'
import {filterValidChars} from '@/utils'
import {createCaptchaVerifier} from '@/firebase'
import {email} from 'vee-validate/dist/rules';

export default {
  data() {
    return {
      phoneNumber: '',
      parsedPhoneNumber: '',
      verificationCode: '',
      countryCode: '',
      smsConfirmationSent: false,
      firebaseConfirmationResult: null,
      loading: false,
      showDialog: false,
      dialogTitle: '',
      dialogMessage: '',
      icons: {mdiPencil, mdiArrowLeft},
      OWN_SMS_METHOD: 'ownSmsMethod',
      FIREBASE_SMS_METHOD: 'firebaseSmsMethod'
    }
  },
  computed: {
    ...mapGetters([
      'authSettings',
      'extensionSettings',
      'isAuthenticated',
      'firebaseConfig'
    ]),
    ...mapState(['userIdentifierForVerification', 'firebaseResult']),
    smsExtensionEnabled() {
      return 'sms' in this.extensionSettings
    },
    isFirebaseSmsAuthEnabled() {
      return _.get(
        this.authSettings, 'enable_firebase_phone_auth', false
      )
    },
    isOwnSmsAuthEnabled() {
      return _.get(this.authSettings, 'enable_sms_login', false)
    },
    smsAuthMethod() {
      if (this.isOwnSmsAuthEnabled) {
        return this.OWN_SMS_METHOD
      }

      if (this.isFirebaseSmsAuthEnabled) {
        return this.FIREBASE_SMS_METHOD
      }

      return ''
    },
    formTitle() {
      return this.smsConfirmationSent
        ? this.$t('smsSignIn.stepTwoTitle')
        : this.$t('smsSignIn.stepOneTitle')
    },
    otpFlow() {
      return this.$route.params.type
    },
    emailIdentified() {
      return this.userIdentifierForVerification && email.validate(this.userIdentifierForVerification)
    },
    stepTwoDescription() {
      return this.otpFlow && this.userIdentifierForVerification ? this.$t('smsSignIn.stepTwoDescriptionWithEmail') : this.$t('smsSignIn.stepTwoDescription')
    }
  },
  mounted() {
    if (this.smsAuthMethod === this.FIREBASE_SMS_METHOD) {
      createCaptchaVerifier()
    }
    if (this.otpFlow) {
      this.goToSecondStep()
    }
  },
  methods: {
    ...mapActions([
      'tosScreen',
      'requestOTP',
      'verifyOTP',
      'verifyEmailOTP',
      'firebaseRequestOTP',
      'firebaseVerifyOTP'
    ]),
    async submitPhoneNumber() {
      this.$refs.smsStepOneForm.validate()
        .then((success) => {
          if (!success) {
            return
          }

          const parsedPhoneNumber = parsePhoneNumberFromString(
            filterValidChars(this.phoneNumber),
            this.countryCode
          )

          if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
            this.openDialog(
              this.$t('smsSignIn.phoneNumberNotValidTitle'),
              this.$t('smsSignIn.phoneNumberNotValidText')
            )
            return
          }

          this.parsedPhoneNumber = parsedPhoneNumber
          this.openTosScreen()
        })
    },
    async requestVerificationCode() {
      try {
        this.loading = true
        this.closeTosScreen()

        if (this.smsAuthMethod === this.OWN_SMS_METHOD) {
          await this.requestOTP(
            this.parsedPhoneNumber.number
          )
        } else if (this.smsAuthMethod === this.FIREBASE_SMS_METHOD) {
          this.firebaseConfirmationResult =
            await this.firebaseRequestOTP({
              phoneNumber: this.parsedPhoneNumber.number,
              recaptchaVerifier: window.recaptchaVerifier
            })
          this.$store.commit('setFirebaseResult', this.firebaseConfirmationResult)
        }

        this.loading = false
        this.goToSecondStep()
      } catch (error) {
        this.loading = false
        this.openDialog(
          this.$t('smsSignIn.signInWithPhoneNumberErrorTitle'),
          this.$t('smsSignIn.signInWithPhoneNumberErrorText')
        )
      }
    },
    submitVerificationCode() {
      this.$refs.smsStepTwoForm.validate()
        .then((success) => {
          if (!success) {
            return
          }

          this.verifyVerificationCode()
        })
    },
    async verifyVerificationCode() {
      try {
        this.loading = true
        if (this.otpFlow) {
          let payload = {
            verificationCode: this.verificationCode,
            loginOnly: true
          }
          if (this.emailIdentified) {
            payload["email"] = this.userIdentifierForVerification
            await this.verifyEmailOTP(payload)
          } else {
            payload["phoneNumber"] = this.userIdentifierForVerification
            if (this.smsAuthMethod === this.OWN_SMS_METHOD) {
              await this.verifyOTP(payload)
            } else if (this.smsAuthMethod === this.FIREBASE_SMS_METHOD) {
              await this.firebaseVerifyOTP({
                confirmationResult: this.firebaseResult,
                verificationCode: this.verificationCode,
                loginOnly: true
              })
            }
          }
        } else if (this.smsAuthMethod === this.OWN_SMS_METHOD) {
          await this.verifyOTP({
            phoneNumber: this.parsedPhoneNumber.number || this.userIdentifierForVerification,
            verificationCode: this.verificationCode,
            loginOnly: true
          })
        } else if (this.smsAuthMethod === this.FIREBASE_SMS_METHOD) {
          await this.firebaseVerifyOTP({
            confirmationResult: this.firebaseResult,
            verificationCode: this.verificationCode,
            loginOnly: true
          })
        }

        this.loading = false

        if (this.isAuthenticated) {
          if (this.$store.getters.isOpenIdProvider) {
            this.$router.push({name: 'openid'});
          } else {
            this.$router.push({name: 'home'})
          }
        }
      } catch (error) {
        this.handleVerificationError(error)
      }
    },
    handleVerificationError(error) {
      const errorCode = _.get(error, 'code')
      const customAuthenticationScreen = _.get(
        this.authSettings, 'custom_authentication_screen', {}
      )
      const followUpScreens = _.get(
        customAuthenticationScreen, 'screens', {}
      )
      const customLoginScreenKey = _.get(followUpScreens, errorCode)

      if (customLoginScreenKey) {
        this.$router.push({
          name: 'customer-login',
          params: {
            screen: customLoginScreenKey,
            identifier: this.parsedPhoneNumber.number || this.userIdentifierForVerification,
            verificationCode: this.verificationCode
          }
        })
      } else {
        this.loading = false
        this.openDialog(
          this.$t('smsSignIn.codeVerificationErrorTitle'),
          this.$t('smsSignIn.codeVerificationErrorText')
        )
      }
    },
    onSelectCountryCode({iso2}) {
      this.countryCode = iso2
    },
    goToFirstStep() {
      if (this.userIdentifierForVerification) {
        this.$router.push({name: 'signin'})
      } else {
        this.smsConfirmationSent = false
      }
    },
    goToSecondStep() {
      this.smsConfirmationSent = true
    },
    openDialog(title, message) {
      this.dialogTitle = title
      this.dialogMessage = message
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
      this.dialogTitle = ''
      this.dialogMessage = ''
    },
    openTosScreen() {
      this.tosScreen({
        show: true,
        acceptAction: this.requestVerificationCode,
        rejectAction: this.closeTosScreen
      })
    },
    closeTosScreen() {
      this.tosScreen({show: false})
    }
  },
}
</script>

<style lang="scss" scoped>
.sms-signin {
  max-width: 560px;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
}

.phone-number-button {
  color: var(--primary);
}
</style>
